import React from 'react';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import { getFileSize } from '../../../utils/commonUtils';
import axiosInstance from '../../../apiServices/axios';
import { IRL_DOC } from '../../../apiServices/api_routes';
import { activeIndexingFileIds, failedIndexingFileIds } from '../../../utils/vdrIndexingQueue';
import { RetryDocumentIndex, markFilesIndexing } from '../../../redux/documentsSlice';
import { isRagIndexableFileName } from '../../../utils/vdrUploadUtils';
import processingCheckIcon from '../assets/processing-check.svg';
import { GetAIDocumentAnalysis, GetLatestDocumentAnalysis } from '../../../redux/dataroomSlice';
import styles from '../css/vdr.module.css';
import { StatusDot, normalizeStatus } from '../components/StatusUI';
import { VDRDocumentIcon, fileTypeLabel } from '../components/VDRItemIcons';
import { readersFromFile, snapshotText } from '../liveData';
import ReadersPanel from './ReadersPanel';
import RelatedIrlObsPanel from './RelatedIrlObsPanel';
import VersionHistoryPanel from './VersionHistoryPanel';
import UploadNewVersionModal from '../modals/UploadNewVersionModal';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDate = (value, withTime = false) => {
    if (!value) return null;
    try {
        return format(new Date(value), withTime ? 'dd MMM yyyy · HH:mm' : 'dd MMM yyyy');
    } catch {
        return null;
    }
};

const formatUtcTimestamp = value => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const pad = part => String(part).padStart(2, '0');
    return `${pad(date.getUTCDate())} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()} · ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())} UTC`;
};

const issueCopy = raw => {
    const text = String(raw || '').trim();
    if (/password|encrypted|protected/i.test(text)) {
        return 'This file is password-protected and could not be processed by Lens.';
    }
    if (text && text.toLowerCase() !== 'indexing failed') return text;
    return 'This file could not be processed by Lens.';
};

const MIME_BY_EXT = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    csv: 'text/csv',
    txt: 'text/plain',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    zip: 'application/zip',
};

const versionToken = file => {
    const raw = file?.version_label || file?.current_version || file?.version;
    if (raw == null || raw === '') return 'v1';
    const text = String(raw);
    return /^v/i.test(text) ? text : `v${text}`;
};

const MetaStack = ({ label, value }) => {
    if (value == null || value === '' || value === '—') return null;
    return (
        <div className={styles.metaStack}>
            <div className={styles.metaStackLabel}>{label}</div>
            <div className={styles.metaStackValue}>{value}</div>
        </div>
    );
};

const FileDetailsPanel = ({
    file,
    workstream,
    folder,
    onClose,
    onOpenFile,
    onDownload,
    onOpenRelated,
    projectUsersAll,
    indexingFiles = [],
    selectedProject,
    relatedIrl = [],
    relatedObs = [],
    onFileUpdated,
}) => {
    const dispatch = useDispatch();
    const panelRef = React.useRef(null);
    const [viewMode, setViewMode] = React.useState('details');
    const [versions, setVersions] = React.useState([]);
    const [uploadVersionOpen, setUploadVersionOpen] = React.useState(false);

    React.useLayoutEffect(() => {
        const panel = panelRef.current;
        if (!panel) return undefined;
        const sync = () => {
            const host = panel.closest('[class*="body"]');
            const top = host ? host.getBoundingClientRect().top : 0;
            panel.style.top = `${Math.max(0, Math.round(top))}px`;
        };
        sync();
        window.addEventListener('resize', sync);
        return () => window.removeEventListener('resize', sync);
    }, []);
    const [snapshot, setSnapshot] = React.useState('');
    const [snapshotLoading, setSnapshotLoading] = React.useState(false);

    React.useEffect(() => {
        setViewMode('details');
        setSnapshot('');
    }, [file?.id]);

    React.useEffect(() => {
        if (!file?.id || !workstream?.id || !selectedProject?.id) return undefined;
        let cancelled = false;
        dispatch(
            GetLatestDocumentAnalysis({
                projectId: selectedProject.id,
                wsId: workstream.id,
                documentId: file.id,
                quickSnapshot: true,
            }),
        )
            .unwrap()
            .then(data => {
                if (!cancelled) setSnapshot(snapshotText(data));
            })
            .catch(() => {
                if (!cancelled) setSnapshot('');
            });
        return () => {
            cancelled = true;
        };
    }, [dispatch, file?.id, selectedProject?.id, workstream?.id]);

    const loadVersions = React.useCallback(async () => {
        if (!file?.id || !workstream?.id) {
            setVersions([]);
            return [];
        }
        const projectId = selectedProject?.id || localStorage.getItem('project_id');
        const response = await axiosInstance.get(`${IRL_DOC}${projectId}/${workstream.id}/${file.id}/versions/`);
        const next = response.data?.versions || [];
        setVersions(next);
        return next;
    }, [file?.id, selectedProject?.id, workstream?.id]);

    React.useEffect(() => {
        let cancelled = false;
        loadVersions().catch(() => {
            if (!cancelled) setVersions([]);
        });
        return () => {
            cancelled = true;
        };
    }, [loadVersions]);

    if (!file) return null;

    const fileName = file.document_name || file.name || file.file_name || 'Untitled';
    const extension =
        file.extension ||
        String(fileName)
            .split('.')
            .pop() ||
        '';

    const indexingIds = new Set(activeIndexingFileIds(indexingFiles, { projectId: selectedProject?.id }));
    const failedIds = new Set(failedIndexingFileIds(indexingFiles, { projectId: selectedProject?.id }));
    const queueItem = (indexingFiles || []).find(item => Number(item.id) === Number(file.id));
    const isProcessing = indexingIds.has(file.id) || indexingIds.has(Number(file.id));
    const isFailed = failedIds.has(file.id) || failedIds.has(Number(file.id));
    const status = isFailed
        ? 'needs_attention'
        : isProcessing
          ? 'processing'
          : normalizeStatus(file.indexing_status || file.status || file.index_status);

    const getUserName = id => {
        if (id == null) return null;
        const u = projectUsersAll?.find(item => item?.value?.group_member_id === id);
        return u?.value?.full_name || null;
    };

    const uploadedBy = getUserName(file.upload_by) || file.uploaded_by_name || null;
    const readers = readersFromFile(file, projectUsersAll);
    const folderName = folder?.name || (typeof file.ws_folder === 'string' ? file.ws_folder : null);
    const locationLabel = workstream?.name || (folderName ? null : 'Initial Project Documents');
    const processingLocation = [workstream?.name, folderName].filter(Boolean).join(' → ') || locationLabel;
    const sourceLabel = file.source || file.upload_source || (file.document_link && !file.document ? 'Link' : 'Direct VDR upload');
    const showRelated = relatedIrl.length > 0 || relatedObs.length > 0;
    const issueMessage = issueCopy(queueItem?.error || file.indexing_error || file.error_message);
    const byteSize = file.metadata_storage_size ?? file.file_size ?? file.size;
    const fileSize = byteSize != null ? getFileSize(byteSize) : null;
    const mimeType = file.metadata_content_type || file.document_type || MIME_BY_EXT[String(extension).toLowerCase()] || null;
    const author = file.metadata_author || null;
    const uploadedAt = file.upload_date || file.uploaded_at || file.created;
    const documentDate = file.metadata_creation_date || file.document_date;
    const documentModified = file.metadata_storage_last_modified || file.metadata_last_modified || file.modified;
    const application = file.metadata_application || file.producer || file.application;
    const pageCount = file.page_count ?? file.metadata_page_count;
    const processingCompleted = file.processing_completed_at || file.indexed_at;
    const displayVersions = versions.length
        ? versions
        : [
              {
                  id: null,
                  version: 1,
                  current: true,
                  document_name: fileName,
                  document: file.document,
                  created: uploadedAt,
                  uploaded_by_name: uploadedBy || '',
              },
          ];
    const latestVersion = displayVersions.find(item => item.current) || displayVersions[0];
    const versionDate = formatDate(latestVersion?.created || file.version_date || uploadedAt);
    const currentVersion = latestVersion ? `v${latestVersion.version}` : versionToken(file);
    const hasEmbedded = Boolean(documentDate || documentModified || author || application || pageCount != null);

    const panelTitle =
        viewMode === 'versions'
            ? 'Version history'
            : viewMode === 'readers'
            ? 'File readers'
            : viewMode === 'related_irl'
              ? 'Information Requests'
              : viewMode === 'related_obs'
                ? 'Observations'
                : 'File details';

    const handleRetry = () => {
        if (!file?.id || !workstream?.id) {
            toast.error('Unable to retry — missing workstream');
            return;
        }
        dispatch(
            RetryDocumentIndex({
                wsId: workstream.id,
                fileIds: [file.id],
                projectId: selectedProject?.id,
            }),
        );
    };

    const handleDownload = () => {
        if (onDownload) onDownload(file);
        else toast.info('Download not available');
    };

    const handleOpen = () => {
        if (onOpenFile) onOpenFile(file);
    };

    const handleGenerateSnapshot = async () => {
        if (!file?.id || !workstream?.id || !selectedProject?.id) return;
        setSnapshotLoading(true);
        try {
            const data = await dispatch(
                GetAIDocumentAnalysis({
                    projectId: selectedProject.id,
                    wsId: workstream.id,
                    documentId: file.id,
                    documentUrl: file.document || file.document_link,
                    quickSnapshot: true,
                    regenerate: true,
                }),
            ).unwrap();
            setSnapshot(snapshotText(data));
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Could not generate snapshot');
        } finally {
            setSnapshotLoading(false);
        }
    };

    const goBack = () => setViewMode('details');
    const showTopBack = viewMode === 'related_irl' || viewMode === 'related_obs';

    const showFileChrome = viewMode === 'details' || viewMode === 'metadata';
    const showVersionHistory = viewMode === 'versions';

    const openVersion = version => {
        if (!version?.document) {
            toast.info('This version cannot be opened');
            return;
        }
        onOpenFile?.({
            ...file,
            document: version.document,
            document_name: version.document_name || fileName,
            versionPreview: true,
        });
    };

    const downloadVersion = version => {
        onDownload?.({
            ...file,
            document: version.document,
            document_name: version.document_name || fileName,
        });
    };

    const uploadVersion = async ({ file: picked, note }) => {
        if (!workstream?.id) {
            toast.error('Unable to upload a version for this file');
            return;
        }
        const projectId = selectedProject?.id || localStorage.getItem('project_id');
        const body = new FormData();
        body.append('document', picked);
        if (note) body.append('note', note);
        const response = await axiosInstance.post(`${IRL_DOC}${projectId}/${workstream.id}/${file.id}/versions/`, body);
        const next = response.data?.versions || [];
        const storedName = response.data?.document_name || picked.name;
        setVersions(next);
        onFileUpdated?.({
            document_name: storedName,
            document: response.data?.document || file.document,
            version: response.data?.version,
        });
        if (isRagIndexableFileName(storedName)) {
            dispatch(
                markFilesIndexing([
                    {
                        id: file.id,
                        wsId: workstream.id,
                        projectId,
                    },
                ]),
            );
        }
        toast.success('New version uploaded');
    };

    return (
        <aside ref={panelRef} className={`${styles.panel} ${styles.fileDrawer}`}>
            <div className={`${styles.panelHeader} ${styles.fileDrawerHeader}`}>
                <h3 className={`${styles.panelTitle} ${styles.fileDrawerTitle}`}>{panelTitle}</h3>
                <button type="button" className={`${styles.iconBtn} ${styles.fileDrawerClose}`} onClick={onClose} aria-label="Close">
                    <Dismiss20Regular />
                </button>
            </div>

            {showFileChrome ? (
                <div className={styles.fileDrawerIdentity}>
                    <div className={styles.fileDrawerIdentityMain}>
                        <VDRDocumentIcon file={file} extension={extension} className={styles.fileDrawerIcon} />
                        <div className={styles.fileDrawerIdentityCopy}>
                            <h4 className={styles.fileDrawerName}>{fileName}</h4>
                            <div className={styles.fileDrawerStatus}>
                                <StatusDot status={status} />
                            </div>
                        </div>
                    </div>
                    <button type="button" className={styles.fileDrawerOpenBtn} onClick={handleOpen}>
                        Open
                    </button>
                </div>
            ) : null}

            <div className={`${styles.panelBody} ${showVersionHistory ? styles.versionHistoryBody : showFileChrome ? styles.fileDrawerBody : ''}`}>
                {showTopBack ? (
                    <div className={styles.panelSubNav}>
                        <button type="button" className={styles.textLink} onClick={goBack}>
                            ← Back
                        </button>
                        <span className={styles.panelSubNavTitle}>{panelTitle}</span>
                    </div>
                ) : null}

                {showVersionHistory ? (
                    <VersionHistoryPanel
                        versions={displayVersions}
                        onOpen={openVersion}
                        onDownload={downloadVersion}
                        onBack={goBack}
                    />
                ) : null}

                {viewMode === 'readers' ? <ReadersPanel fileName={fileName} readers={readers} /> : null}

                {viewMode === 'related_irl' || viewMode === 'related_obs' ? (
                    <RelatedIrlObsPanel
                        mode={viewMode}
                        items={viewMode === 'related_irl' ? relatedIrl : relatedObs}
                        onOpen={item => onOpenRelated?.(viewMode, item)}
                    />
                ) : null}

                {showFileChrome && (
                    <>
                        {status === 'ready' && workstream?.id ? (
                            <div className={styles.fileDrawerSection}>
                                <h4 className={styles.fileDrawerHeading}>Quick snapshot</h4>
                                {snapshot ? (
                                    <p className={styles.fileDrawerSnapshot}>{snapshot}</p>
                                ) : snapshotLoading ? (
                                    <p className={styles.fileDrawerMuted}>Generating snapshot…</p>
                                ) : (
                                    <button type="button" className={`${styles.textLink} ${styles.fileDrawerGenerate}`} onClick={handleGenerateSnapshot}>
                                        Generate snapshot
                                    </button>
                                )}
                            </div>
                        ) : null}

                        {status === 'ready' && locationLabel ? (
                            <div className={workstream?.id ? styles.fileDrawerBlockFlush : styles.fileDrawerBlock}>
                                <h4 className={styles.fileDrawerHeading}>Location</h4>
                                <div className={styles.fileDrawerValue}>{locationLabel}</div>
                            </div>
                        ) : null}

                        {status !== 'ready' && processingLocation ? (
                            <div className={styles.fileDrawerBlockFlush}>
                                <h4 className={styles.fileDrawerHeading}>Location</h4>
                                <div className={styles.fileDrawerValue}>{processingLocation}</div>
                            </div>
                        ) : null}

                        {status === 'processing' ? (
                            <div className={styles.fileDrawerBlock}>
                                <h4 className={styles.fileDrawerHeading}>Lens processing</h4>
                                <p className={styles.processingIntro}>The file is being prepared for search and analysis.</p>
                                <div className={styles.processingSteps}>
                                    <div className={styles.processingStepDone}>
                                        <span className={styles.processingCheck}>
                                            <img src={processingCheckIcon} alt="" width="8" height="6" />
                                        </span>
                                        Stored
                                    </div>
                                    <div className={styles.processingStepDone}>
                                        <span className={styles.processingCheck}>
                                            <img src={processingCheckIcon} alt="" width="8" height="6" />
                                        </span>
                                        Security scan
                                    </div>
                                    <div className={styles.processingStepActive}>
                                        <span className={styles.processingActiveDot} aria-hidden />
                                        Extracting and indexing
                                    </div>
                                </div>
                                <p className={styles.processingHint}>You can continue working while processing completes.</p>
                            </div>
                        ) : null}

                        {status === 'needs_attention' ? (
                            <div className={styles.fileDrawerBlock}>
                                <h4 className={styles.fileDrawerHeading}>Issue</h4>
                                <p className={styles.issueText}>{issueMessage}</p>
                                <div className={styles.issueActions}>
                                    <button type="button" className={`${styles.primaryBtn} ${styles.issueAction}`} onClick={handleRetry}>
                                        Retry processing
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.secondaryBtn} ${styles.issueAction} ${styles.issueReplace}`}
                                        onClick={() => setUploadVersionOpen(true)}
                                    >
                                        Replace file
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        {status === 'ready' ? (
                            <>
                                <div className={styles.fileDrawerBlock}>
                                    <h4 className={styles.fileDrawerHeading}>File information</h4>
                                    <div className={styles.fileDrawerFields}>
                                        <MetaStack label="Document date" value={formatDate(documentDate)} />
                                        <MetaStack label="Uploaded" value={formatDate(uploadedAt, true)} />
                                        <MetaStack label="Uploaded by" value={uploadedBy} />
                                        <MetaStack label="File size" value={fileSize} />
                                        <MetaStack label="File type" value={fileTypeLabel(extension)} />
                                        <MetaStack label="Source" value={sourceLabel} />
                                    </div>
                                    <button
                                        type="button"
                                        className={`${styles.textLink} ${styles.fileDrawerLink} ${styles.fileDrawerMetaLink}`}
                                        onClick={() => setViewMode(viewMode === 'metadata' ? 'details' : 'metadata')}
                                    >
                                        {viewMode === 'metadata' ? 'Less metadata ↑' : 'More metadata →'}
                                    </button>
                                    {viewMode === 'metadata' ? (
                                        <div className={styles.fileDrawerMetaExpand}>
                                            <h4 className={styles.fileDrawerHeading}>System metadata</h4>
                                            <div className={styles.fileDrawerFieldsTight}>
                                                <MetaStack label="Original filename" value={fileName} />
                                                <MetaStack label="MIME type" value={mimeType} />
                                                <MetaStack
                                                    label="File size"
                                                    value={
                                                        byteSize != null
                                                            ? `${Number(byteSize).toLocaleString()} bytes${fileSize ? ` (${fileSize})` : ''}`
                                                            : null
                                                    }
                                                />
                                                <MetaStack label="Upload timestamp" value={formatUtcTimestamp(uploadedAt)} />
                                                <MetaStack label="Uploaded by" value={uploadedBy} />
                                                <MetaStack label="Current version" value={currentVersion} />
                                                <MetaStack label="Processing completed" value={formatUtcTimestamp(processingCompleted)} />
                                            </div>
                                            {hasEmbedded ? (
                                                <>
                                                    <h4 className={`${styles.fileDrawerHeading} ${styles.fileDrawerEmbeddedHeading}`}>
                                                        Embedded document metadata
                                                    </h4>
                                                    <div className={styles.fileDrawerFieldsTight}>
                                                        <MetaStack label="Document created" value={formatDate(documentDate)} />
                                                        <MetaStack label="Document modified" value={formatDate(documentModified)} />
                                                        <MetaStack label="Author / creator" value={author} />
                                                        <MetaStack label="Application" value={application} />
                                                        <MetaStack
                                                            label="Page count"
                                                            value={pageCount != null ? `${pageCount} page${Number(pageCount) === 1 ? '' : 's'}` : null}
                                                        />
                                                    </div>
                                                    <p className={styles.fileDrawerNote}>
                                                        Embedded document metadata is provided by the source file and may not always be reliable.
                                                    </p>
                                                </>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>

                                <div className={styles.fileDrawerBlock}>
                                    <h4 className={styles.fileDrawerHeading}>Activity</h4>
                                    <div className={styles.fileDrawerActivity}>
                                        <div>
                                            <div>
                                                Read by <span className={styles.fileDrawerReadCount}>{readers.length}</span>
                                            </div>
                                            {readers[0]?.lastRead && readers[0].lastRead !== '—' ? (
                                                <div className={styles.fileDrawerLastRead}>Last read {readers[0].lastRead}</div>
                                            ) : null}
                                        </div>
                                        <button type="button" className={`${styles.textLink} ${styles.fileDrawerLink}`} onClick={() => setViewMode('readers')}>
                                            View readers →
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.fileDrawerBlock}>
                                    <h4 className={styles.fileDrawerHeading}>Versions</h4>
                                    <div className={styles.fileDrawerValue}>
                                        Current version {currentVersion}
                                        {versionDate ? ` · ${versionDate}` : ''}
                                    </div>
                                    <button
                                        type="button"
                                        className={`${styles.textLink} ${styles.fileDrawerLink} ${styles.fileDrawerMetaLink}`}
                                        onClick={() => setViewMode('versions')}
                                    >
                                        View version history →
                                    </button>
                                </div>

                                <div className={styles.fileDrawerBlock}>
                                    <h4 className={styles.fileDrawerHeading}>Access</h4>
                                    <div className={styles.fileDrawerValue}>{file.access_label || 'Inherited from project'}</div>
                                </div>

                                {showRelated ? (
                                    <div className={styles.fileDrawerBlock}>
                                        <h4 className={styles.fileDrawerHeading}>Related</h4>
                                        <button
                                            type="button"
                                            className={styles.relatedRow}
                                            style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
                                            onClick={() => setViewMode('related_irl')}
                                        >
                                            <span>Information Requests</span>
                                            <span className={styles.relatedCount}>{relatedIrl.length} →</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.relatedRow}
                                            style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
                                            onClick={() => setViewMode('related_obs')}
                                        >
                                            <span>Observations</span>
                                            <span className={styles.relatedCount}>{relatedObs.length} →</span>
                                        </button>
                                    </div>
                                ) : null}
                            </>
                        ) : null}
                    </>
                )}
            </div>

            {showVersionHistory ? (
                <div className={styles.versionUploadFooter}>
                    <button type="button" className={styles.versionUploadBtn} onClick={() => setUploadVersionOpen(true)}>
                        Upload new version
                    </button>
                </div>
            ) : null}

            {showFileChrome && (
                <div className={`${styles.panelFooter} ${styles.fileDrawerFooter}`}>
                    <button type="button" className={styles.primaryBtn} onClick={handleOpen}>
                        Open
                    </button>
                    <button type="button" className={`${styles.secondaryBtn} ${styles.fileDrawerDownload}`} onClick={handleDownload}>
                        Download
                    </button>
                    <button type="button" className={styles.fileDrawerMore} aria-label="More">
                        ···
                    </button>
                </div>
            )}

            {viewMode === 'readers' ? (
                <div className={styles.panelFooter}>
                    <button type="button" className={styles.textLink} onClick={goBack}>
                        ← Back to file details
                    </button>
                </div>
            ) : null}
            <UploadNewVersionModal
                open={uploadVersionOpen}
                onClose={() => setUploadVersionOpen(false)}
                file={file}
                fileName={fileName}
                currentVersionLabel={currentVersion}
                onUpload={uploadVersion}
            />
        </aside>
    );
};

export default FileDetailsPanel;
